import { useState, useEffect, useCallback, useMemo } from 'react';
import { useDatosCacheados } from '@/hooks/useDatosCacheados';
import {
  claveDashboard,
  mapearRpcDashboard,
  rangoPorDefectoDashboard,
  type SerieSparkline,
} from '@/lib/dashboardQuery';
import {
  Page,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Box,
  Badge,
} from '@/lib/polaris';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@smarty/shared';
import { startOfDay, endOfDay, subDays, parseISO, isSameDay, eachDayOfInterval } from 'date-fns';
import { HomeIcon } from '@/lib/polaris-icons';
import { PolarisDateRangePopover, type PolarisDateRangeValue } from '@/components/polaris/PolarisDateRangePopover';
import { PolarisChannelPopover, type ChannelValue } from '@/components/polaris/PolarisChannelPopover';
import { PageBreadcrumbTitle } from '@/components/polaris/PageBreadcrumbTitle';
import { KpiBarV2, type KpiV2Item } from '@/components/admin/KpiBarV2';
import { KpiDetailChart } from '@/components/admin/KpiDetailChart';
import { ROUTES } from '@/config/routes.config';
import { useStoreNavigate } from '@/hooks/useStoreNavigate';
import { devLog } from '@/lib/utils';

interface DashboardStats {
  salesInPeriod: number;
  previousPeriodSales: number;
  ordersInPeriod: number;
  pendingOrders: number;
  totalProducts: number;
  activeProducts: number;
  totalCustomers: number;
  newCustomersInPeriod: number;
}

/** Órdenes en el periodo (para sparklines y derivar clientes) */
interface OrderForStats {
  created_at: string;
  total: number;
  status: string | null;
  customer_name?: string | null;
  customer_phone?: string | null;
}

/** Clientes nuevos en el periodo (para sparkline por día) */
interface CustomerForStats {
  created_at: string;
}

/** Productos en el periodo (para sparkline por día) */
interface ProductForStats {
  created_at: string;
}

/** Lo que el Inicio necesita para pintarse. Viaja JUNTO en una sola entrada de caché: mostrar los
 *  KPIs sin sus series, o al revés, es un tablero a medias. */
interface DatosPanel {
  stats: DashboardStats;
  sparkline: SerieSparkline | null;
  orders: OrderForStats[];
  products: ProductForStats[];
  customers: CustomerForStats[];
}

const ESTADISTICAS_VACIAS: DashboardStats = {
  salesInPeriod: 0,
  previousPeriodSales: 0,
  ordersInPeriod: 0,
  pendingOrders: 0,
  totalProducts: 0,
  activeProducts: 0,
  totalCustomers: 0,
  newCustomersInPeriod: 0,
};

export default function DashboardPage() {
  const navigate = useStoreNavigate();
  const { tenantId, loading: authLoading } = useAuth();
  const today = startOfDay(new Date());
  // 🪤 El rango sale del helper compartido, no calculado acá: la precarga del Sidebar usa el mismo.
  // Si cada uno lo calcula por su lado, las claves difieren y la precarga calienta una entrada que
  // la pantalla nunca lee — el usuario paga el RPC dos veces y no gana nada.
  const [selectedDates, setSelectedDates] = useState<PolarisDateRangeValue>(() => rangoPorDefectoDashboard());
  const [channelFilter, setChannelFilter] = useState<ChannelValue>('all');
  // Indicador seleccionado para el cuadro de detalle (estilo Shopify).
  const [selectedMetric, setSelectedMetric] = useState<string>('sales');
  const channelOptions: Array<{ value: ChannelValue; label: string }> = [
    { value: 'all', label: 'Todos' },
    { value: 'ecommerce', label: 'Tienda online' },
    { value: 'pos', label: 'POS' },
  ];

  // Serie del PERIODO ANTERIOR (buckets por día) para la línea punteada de comparación
  // del cuadro de detalle (estilo Shopify). Client-side: una consulta ligera, funciona
  // tanto en el camino RPC como en el fallback.
  const [prevSeries, setPrevSeries] = useState<Record<'sales' | 'orders' | 'pending' | 'customers' | 'new_customers', number[]> | null>(null);
  // Totales del periodo anterior (para el delta % de cada métrica de flujo).
  const [prevTotals, setPrevTotals] = useState<{ sales: number; orders: number; customers: number } | null>(null);


  /**
   * Por el camino pavimentado de la caché (ADR-0012, paso 3). Ver `useDatosCacheados`.
   *
   * ⭐ Esta pantalla es la que más se abre del panel y era la ÚLTIMA sin caché de verdad: tenía la
   * suya, a mano (`dashboardStatsCache`, 45 s, revalidación en segundo plano). La cuarta caché
   * casera del panel — y se elimina acá.
   *
   * 🪤 Y por eso el RUM la medía mal: `useIsFetching()` sólo ve consultas DE LA CACHÉ, así que una
   * pantalla que carga por su cuenta se declara terminada apenas pinta. El «978 ms / 0% de caché»
   * que se midió eran las consultas del layout, no el dashboard. Al entrar acá, el número empieza a
   * decir la verdad — y es esperable que EMPEORE antes de mejorar.
   *
   * 🪤 El camino alternativo (cuando el RPC no está disponible) vive DENTRO de la consulta. Si
   * quedara fuera, la caché guardaría el resultado del RPC fallido y el alternativo se recalcularía
   * en cada render sin quedar guardado nunca.
   */
  const { datos: panel, cargando: loading, recargar: loadDashboardStats } = useDatosCacheados<DatosPanel>({
    clave: claveDashboard(tenantId, selectedDates?.start, selectedDates?.end, channelFilter),
    activo: !!tenantId && !authLoading && !!selectedDates?.start,
    // 45 s, el mismo valor que tenía la caché a mano: es un tablero, no una lista que se edita.
    frescura: 45_000,
    mantenerAnterior: true,
    traer: async (): Promise<DatosPanel> => {
      const startDate = startOfDay(selectedDates!.start!);
      const endDate = selectedDates!.end ? endOfDay(selectedDates!.end) : endOfDay(selectedDates!.start!);

      const { data: rpcData, error: rpcError } = await supabase.rpc('get_dashboard_stats', {
        p_tenant_id: tenantId,
        p_start_date: startDate.toISOString(),
        p_end_date: endDate.toISOString(),
        p_channel: channelFilter,
      });

      if (!rpcError && rpcData && typeof rpcData === 'object') {
        const { stats, sparkline } = mapearRpcDashboard(rpcData as Record<string, unknown>);
        return { stats, sparkline, orders: [], products: [], customers: [] };
      }

        const periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000)) || 1;
        const previousEnd = new Date(startDate.getTime() - 1);
        const previousStart = subDays(previousEnd, periodDays - 1);
        const previousStartNorm = startOfDay(previousStart);
        const previousEndNorm = endOfDay(previousEnd);

        // Fallback (dominio único orders_v2): aliasing grand_total→total, lifecycle_stage→status,
        // y la relación order_lines expuesta como order_items para no cambiar el consumidor.
        const orderSelect = 'id, order_number, created_at, total:grand_total, status:lifecycle_stage, customer_name, customer_phone, payment_status, order_items:order_lines (order_id, subtotal:line_total, quantity:qty, unit_price)';
        const v2Channel = channelFilter === 'ecommerce' ? 'online_checkout' : channelFilter;
        const runOrdersWithItemsQuery = (from: Date, to: Date) => {
          let q = supabase
            .from('orders_v2')
            .select(orderSelect)
            .eq('tenant_id', tenantId)
            .gte('created_at', from.toISOString())
            .lte('created_at', to.toISOString());
          if (channelFilter !== 'all') {
            q = q.eq('channel', v2Channel);
          }
          return q.order('created_at', { ascending: false });
        };

        const [
          ordersInPeriodResult,
          ordersPreviousResult,
          pendingCountResult,
          totalProductsCountResult,
          activeProductsCountResult,
          productsInPeriodResult,
        ] = await Promise.all([
          runOrdersWithItemsQuery(startDate, endDate),
          runOrdersWithItemsQuery(previousStartNorm, previousEndNorm),
          supabase
            .from('orders_v2')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenantId)
            .in('lifecycle_stage', ['quotation', 'sale']),
          supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenantId),
          supabase
            .from('products')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenantId)
            .eq('is_active', true),
          supabase
            .from('products')
            .select('created_at')
            .eq('tenant_id', tenantId)
            .gte('created_at', startDate.toISOString())
            .lte('created_at', endDate.toISOString()),
        ]);

        const ordersInPeriodData = (ordersInPeriodResult.data ?? []) as Array<{
          id: string;
          created_at: string;
          total?: unknown;
          status?: string | null;
          customer_name?: string | null;
          customer_phone?: string | null;
          order_items?: Array<{ order_id: string; subtotal?: unknown; quantity?: unknown; unit_price?: unknown }> | null;
        }>;
        const ordersPreviousData = (ordersPreviousResult.data ?? []) as Array<{
          id: string;
          total?: unknown;
          order_items?: Array<{ subtotal?: unknown; quantity?: unknown; unit_price?: unknown }> | null;
        }>;
        const productsInPeriodData = productsInPeriodResult.data ?? [];

        const parseTotal = (v: unknown): number => {
          if (v == null) return 0;
          if (typeof v === 'number' && !Number.isNaN(v)) return v;
          const n = parseFloat(String(v));
          return Number.isNaN(n) ? 0 : n;
        };

        const totalsByOrderId = new Map<string, number>();
        let salesInPeriod = 0;
        let previousPeriodSales = 0;

        for (const order of ordersInPeriodData) {
          const items = order.order_items ?? [];
          let orderTotal = 0;
          if (items.length > 0) {
            for (const row of items) {
              const sub = parseTotal(row.subtotal);
              const qty = parseTotal(row.quantity);
              const up = parseTotal(row.unit_price);
              orderTotal += sub > 0 ? sub : qty * up;
            }
            totalsByOrderId.set(order.id, orderTotal);
          } else {
            orderTotal = parseTotal(order.total);
            totalsByOrderId.set(order.id, orderTotal);
          }
          salesInPeriod += orderTotal;
        }

        for (const order of ordersPreviousData) {
          const items = order.order_items ?? [];
          if (items.length > 0) {
            previousPeriodSales += (items as Array<{ subtotal?: unknown; quantity?: unknown; unit_price?: unknown }>).reduce(
              (s, row) => s + (parseTotal(row.subtotal) > 0 ? parseTotal(row.subtotal) : parseTotal(row.quantity) * parseTotal(row.unit_price)),
              0
            );
          } else {
            previousPeriodSales += parseTotal(order.total);
          }
        }

        const customerKey = (o: { customer_name?: string | null; customer_phone?: string | null }) => {
          const name = (o.customer_name || '').trim();
          if (!name) return null;
          const phone = o.customer_phone ?? '';
          return phone ? `${name}_${phone}` : name;
        };
        const uniqueCustomersInPeriod = new Set(
          ordersInPeriodData.map(customerKey).filter((k): k is string => k != null)
        );
        const totalCustomers = uniqueCustomersInPeriod.size;
        const newCustomersInPeriod = totalCustomers;

        const pendingOrders = pendingCountResult.error ? 0 : (pendingCountResult.count ?? 0);

      const totalProducts = totalProductsCountResult.error ? 0 : (totalProductsCountResult.count ?? 0);
      const activeProducts = activeProductsCountResult.error ? 0 : (activeProductsCountResult.count ?? 0);

      return {
        stats: {
          salesInPeriod,
          previousPeriodSales,
          ordersInPeriod: ordersInPeriodData.length,
          pendingOrders,
          totalProducts,
          activeProducts,
          totalCustomers,
          newCustomersInPeriod,
        },
        sparkline: null,
        orders: ordersInPeriodData.map((o) => ({
          created_at: o.created_at,
          total: totalsByOrderId.get(o.id) ?? parseTotal(o.total),
          status: o.status ?? null,
          customer_name: o.customer_name ?? null,
          customer_phone: o.customer_phone ?? null,
        })),
        products: productsInPeriodData,
        customers: [],
      };
    },
  });

  const stats = panel?.stats ?? ESTADISTICAS_VACIAS;
  const sparklineFromRpc = panel?.sparkline ?? null;
  const ordersForStats = useMemo(() => panel?.orders ?? [], [panel]);
  const productsForStats = useMemo(() => panel?.products ?? [], [panel]);
  const customersForStats = useMemo(() => panel?.customers ?? [], [panel]);


  // Serie del periodo anterior para la línea de comparación (punteada) del detalle.
  // Consulta dedicada y ligera (solo campos para bucketear), independiente del RPC de stats.
  useEffect(() => {
    if (!tenantId || !selectedDates?.start || !selectedDates?.end) { setPrevSeries(null); return; }
    let cancelled = false;
    (async () => {
      const startDate = startOfDay(selectedDates.start);
      const endDate = endOfDay(selectedDates.end);
      const periodDays = Math.ceil((endDate.getTime() - startDate.getTime()) / 86400000) || 1;
      const prevEnd = new Date(startDate.getTime() - 1);
      const prevStartNorm = startOfDay(subDays(prevEnd, periodDays - 1));
      const prevEndNorm = endOfDay(prevEnd);
      const v2Channel = channelFilter === 'ecommerce' ? 'online_checkout' : channelFilter;
      let q = supabase
        .from('orders_v2')
        .select('created_at, total:grand_total, status:lifecycle_stage, customer_name, customer_phone')
        .eq('tenant_id', tenantId)
        .gte('created_at', prevStartNorm.toISOString())
        .lte('created_at', prevEndNorm.toISOString());
      if (channelFilter !== 'all') q = q.eq('channel', v2Channel);
      const { data, error } = await q;
      if (cancelled) return;
      if (error || !data) { setPrevSeries(null); return; }
      const rows = data as Array<{ created_at: string; total?: unknown; status?: string | null; customer_name?: string | null; customer_phone?: string | null }>;
      const days = eachDayOfInterval({ start: prevStartNorm, end: startOfDay(prevEnd) });
      const sampled = days.length > 30 ? days.slice(-30) : days;
      const parseT = (v: unknown): number => { const n = typeof v === 'number' ? v : parseFloat(String(v ?? '')); return Number.isNaN(n) ? 0 : n; };
      // "Pendiente" = pedido en curso. Mismos estados que el RPC get_dashboard_stats
      // (lifecycle_stage 'quotation'/'sale') para que la comparación sea consistente.
      const isPending = (s: string | null | undefined) => ['quotation', 'sale'].includes(String(s || '').toLowerCase());
      const custKey = (o: { customer_name?: string | null; customer_phone?: string | null }) => {
        const n = (o.customer_name || '').trim(); if (!n) return null; const p = o.customer_phone ?? ''; return p ? `${n}_${p}` : n;
      };
      const onDay = (d: Date) => rows.filter((o) => isSameDay(parseISO(o.created_at), d));
      const sales = sampled.map((d) => onDay(d).reduce((s, o) => s + parseT(o.total), 0));
      const orders = sampled.map((d) => onDay(d).length);
      const pending = sampled.map((d) => onDay(d).filter((o) => isPending(o.status)).length);
      const customers = sampled.map((d) => new Set(onDay(d).map(custKey).filter((k): k is string => k != null)).size);
      setPrevSeries({ sales, orders, pending, customers, new_customers: customers });
      // Totales del periodo anterior (distintos de la suma de buckets para 'customers').
      setPrevTotals({
        sales: rows.reduce((s, o) => s + parseT(o.total), 0),
        orders: rows.length,
        customers: new Set(rows.map(custKey).filter((k): k is string => k != null)).size,
      });
    })();
    return () => { cancelled = true; };
  }, [tenantId, selectedDates?.start, selectedDates?.end, channelFilter]);

  // Sparklines: desde RPC (una ida y vuelta) o calculados en cliente cuando se usa fallback
  const sparklineData = useMemo(() => {
    if (sparklineFromRpc) {
      return sparklineFromRpc;
    }
    if (!selectedDates?.start || !selectedDates?.end) {
      return { sales: [0], orders: [0], pending: [0], products: [0], customers: [0], new_customers: [0] };
    }
    const start = startOfDay(selectedDates.start);
    const end = endOfDay(selectedDates.end);
    const days = eachDayOfInterval({ start, end });
    const maxPoints = 30;
    const sampledDays = days.length > maxPoints ? days.slice(-maxPoints) : days;

    // En curso = lifecycle_stage 'quotation'/'sale' (mismo criterio que el RPC).
    const isPendingStatus = (s: string | null) =>
      ['quotation', 'sale'].includes(String(s || '').toLowerCase());

    const sales = sampledDays.map((d) =>
      ordersForStats
        .filter((o) => isSameDay(parseISO(o.created_at), d))
        .reduce((sum, o) => sum + o.total, 0)
    );
    const orders = sampledDays.map((d) =>
      ordersForStats.filter((o) => isSameDay(parseISO(o.created_at), d)).length
    );
    const pending = sampledDays.map((d) =>
      ordersForStats.filter(
        (o) => isSameDay(parseISO(o.created_at), d) && isPendingStatus(o.status)
      ).length
    );
    const products = sampledDays.map((d) =>
      productsForStats.filter((p) => isSameDay(parseISO(p.created_at), d)).length
    );
    const customerKey = (o: OrderForStats) => {
      const name = (o.customer_name || '').trim();
      if (!name) return null;
      const phone = o.customer_phone ?? '';
      return phone ? `${name}_${phone}` : name;
    };
    const customers = sampledDays.map((d) => {
      const keys = new Set(
        ordersForStats
          .filter((o) => isSameDay(parseISO(o.created_at), d))
          .map(customerKey)
          .filter((k): k is string => k != null)
      );
      return keys.size;
    });
    const new_customers = customers;

    return { sales, orders, pending, products, customers, new_customers };
  }, [sparklineFromRpc, selectedDates?.start, selectedDates?.end, ordersForStats, productsForStats]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-PE', {
      style: 'currency',
      currency: 'PEN',
    }).format(amount);
  };

  // KPIs v2 (estilo Shopify) de Inicio. Reusa los stats + series del rango de fechas.
  // Comparación period-over-period: Ventas usa previousPeriodSales (RPC); Pedidos/Ticket/
  // Clientes usan los totales del periodo anterior (prevTotals, consulta cliente).
  // Pendientes y Productos son métricas-snapshot (no de flujo) → sin comparación.
  const kpiV2Items: KpiV2Item[] = useMemo(() => {
    const salesArr = sparklineData['sales'] ?? [];
    const ordersArr = sparklineData['orders'] ?? [];
    const ticketSeries = salesArr.map((s, i) => ((ordersArr[i] ?? 0) > 0 ? s / (ordersArr[i] as number) : 0));
    const avgTicket = stats.ordersInPeriod > 0 ? stats.salesInPeriod / stats.ordersInPeriod : 0;
    const prevOrders = prevTotals?.orders ?? 0;
    const prevTicket = prevOrders > 0 ? stats.previousPeriodSales / prevOrders : 0;
    return [
      { key: 'sales', label: 'Ventas', value: formatCurrency(stats.salesInPeriod), raw: stats.salesInPeriod, previous: stats.previousPeriodSales, series: salesArr },
      { key: 'orders', label: 'Pedidos', value: String(stats.ordersInPeriod), raw: stats.ordersInPeriod, previous: prevOrders, series: ordersArr },
      { key: 'ticket', label: 'Ticket promedio', value: formatCurrency(avgTicket), raw: avgTicket, previous: prevTicket, series: ticketSeries },
      { key: 'pending', label: 'Pendientes', value: String(stats.pendingOrders), raw: stats.pendingOrders, series: sparklineData['pending'] ?? [] },
      { key: 'new_customers', label: 'Nuevos clientes', value: String(stats.newCustomersInPeriod), raw: stats.newCustomersInPeriod, previous: prevTotals?.customers ?? 0, series: sparklineData['new_customers'] ?? [] },
      { key: 'customers', label: 'Clientes', value: String(stats.totalCustomers), raw: stats.totalCustomers, previous: prevTotals?.customers ?? 0, series: sparklineData['customers'] ?? [] },
      { key: 'products', label: 'Productos activos', value: String(stats.activeProducts), raw: stats.activeProducts, series: sparklineData['products'] ?? [] },
    ];
  }, [stats.salesInPeriod, stats.previousPeriodSales, stats.ordersInPeriod, stats.pendingOrders, stats.activeProducts, stats.totalCustomers, stats.newCustomersInPeriod, sparklineData, prevTotals]);

  // Cuadro de detalle (estilo Shopify "Pedidos a lo largo del tiempo"): el indicador
  // seleccionado en el KpiBar, con su serie del periodo y etiquetas de fecha en el eje X.
  const selectedItem = kpiV2Items.find((i) => i.key === selectedMetric) ?? kpiV2Items[0];
  const detailLabels = useMemo(() => {
    const n = selectedItem?.series.length ?? 0;
    if (!n || !selectedDates?.start || !selectedDates?.end) return [];
    const days = eachDayOfInterval({ start: startOfDay(selectedDates.start), end: endOfDay(selectedDates.end) });
    const sampled = days.length > n ? days.slice(-n) : days;
    return Array.from({ length: n }, (_, i) =>
      sampled[i] ? sampled[i].toLocaleDateString('es-PE', { day: '2-digit', month: 'short' }) : String(i + 1)
    );
  }, [selectedItem, selectedDates?.start, selectedDates?.end]);
  const detailDeltaNode = useMemo(() => {
    if (!selectedItem || selectedItem.previous <= 0) return null;
    const pct = ((selectedItem.raw - selectedItem.previous) / selectedItem.previous) * 100;
    const up = pct >= 0;
    return (
      <Text as="span" variant="bodySm" tone={up ? 'success' : 'critical'} fontWeight="medium">
        {`${up ? '↗' : '↘'} ${Math.abs(pct).toFixed(0)}% vs periodo anterior`}
      </Text>
    );
  }, [selectedItem]);

  // Serie del periodo anterior para el indicador seleccionado (línea punteada del detalle),
  // alineada en longitud a la serie actual. 'ticket' se deriva; 'products' no tiene previa.
  const detailComparisonSeries = useMemo(() => {
    if (!prevSeries || !selectedItem) return undefined;
    let prev: number[] | undefined;
    if (selectedMetric === 'ticket') {
      const s = prevSeries.sales; const o = prevSeries.orders;
      prev = s.map((v, i) => ((o[i] ?? 0) > 0 ? v / (o[i] as number) : 0));
    } else if (selectedMetric in prevSeries) {
      prev = prevSeries[selectedMetric as keyof typeof prevSeries];
    }
    if (!prev || prev.length === 0) return undefined;
    const n = selectedItem.series.length;
    if (prev.length > n) prev = prev.slice(-n);
    else if (prev.length < n) prev = [...Array(n - prev.length).fill(0), ...prev];
    return prev;
  }, [prevSeries, selectedMetric, selectedItem]);

  const salesChange = useMemo(() => {
    if (stats.previousPeriodSales === 0) return null;
    const change = ((stats.salesInPeriod - stats.previousPeriodSales) / stats.previousPeriodSales) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      isPositive: change >= 0,
    };
  }, [stats.salesInPeriod, stats.previousPeriodSales]);

  const stat = (value: string | number) => (loading ? '—' : value);

  // Misma pauta que Ventas/Logística/Cobranzas: mostrar siempre el shell; los datos se rellenan en segundo plano (placeholders "—" en KPIs mientras loading).
  return (
    <Page
      title={
        <PageBreadcrumbTitle
          icon={HomeIcon}
          title="Inicio"
          onBack={() => navigate(ROUTES.HOME)}
          backAccessibilityLabel="Volver al inicio"
        />
      }
    >
      <BlockStack gap="400">
        {/* KpiBar v2 (estilo Shopify). Fecha y Canal van como celdas leftContent
            (cada una en su celda, con su divisor) y manejan todos los KPIs. */}
        <KpiBarV2
          items={kpiV2Items}
          loading={loading}
          visibleCount={3}
          selectedKey={selectedMetric}
          onSelectMetric={setSelectedMetric}
          detail={
            selectedItem ? (
              <KpiDetailChart
                label={selectedItem.label}
                value={selectedItem.value}
                series={selectedItem.series}
                labels={detailLabels}
                deltaNode={detailDeltaNode}
                comparisonSeries={detailComparisonSeries}
              />
            ) : undefined
          }
          leftContent={[
            <Box key="fecha" paddingBlock="300" paddingInline="300" borderInlineEndWidth="025" borderColor="border-secondary">
              <PolarisDateRangePopover
                value={selectedDates}
                onChange={setSelectedDates}
                initialPreset="last30days"
                activatorVariant="tertiary"
              />
            </Box>,
            <Box key="canal" paddingBlock="300" paddingInline="300" borderInlineEndWidth="025" borderColor="border-secondary">
              <PolarisChannelPopover
                value={channelFilter}
                onChange={setChannelFilter}
                options={channelOptions}
                activatorVariant="tertiary"
              />
            </Box>,
          ]}
        />

          <BlockStack gap="400">
            <BlockStack gap="300">
              <InlineStack gap="400" wrap>
                <Box minWidth="200px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="bodySm" tone="subdued">
                        Ventas del periodo
                      </Text>
                      <Text variant="heading2xl" fontWeight="bold">
                        {stat(formatCurrency(stats.salesInPeriod))}
                      </Text>
                      {!loading && salesChange != null ? (
                        <InlineStack gap="100" align="start">
                          <Badge tone={salesChange.isPositive ? 'success' : 'critical'}>
                            {salesChange.isPositive ? '↑' : '↓'} {salesChange.value}%
                          </Badge>
                          <Text variant="bodySm" tone="subdued">
                            vs periodo anterior
                          </Text>
                        </InlineStack>
                      ) : null}
                    </BlockStack>
                  </Card>
                </Box>

                <Box minWidth="200px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="bodySm" tone="subdued">
                        Pedidos en el periodo
                      </Text>
                      <Text variant="heading2xl" fontWeight="bold">
                        {stat(stats.ordersInPeriod)}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        {channelFilter === 'all' ? 'Todos los canales' : channelOptions.find((o) => o.value === channelFilter)?.label}
                      </Text>
                    </BlockStack>
                  </Card>
                </Box>

                <Box minWidth="200px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="bodySm" tone="subdued">
                        Órdenes pendientes
                      </Text>
                      <Text variant="heading2xl" fontWeight="bold">
                        {stat(stats.pendingOrders)}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        Requieren atención
                      </Text>
                    </BlockStack>
                  </Card>
                </Box>

                <Box minWidth="200px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="bodySm" tone="subdued">
                        Productos activos
                      </Text>
                      <Text variant="heading2xl" fontWeight="bold">
                        {stat(stats.activeProducts)}
                      </Text>
                      <Text variant="bodySm" tone="subdued">
                        de {stat(stats.totalProducts)} totales
                      </Text>
                    </BlockStack>
                  </Card>
                </Box>

                <Box minWidth="200px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="200">
                      <Text variant="bodySm" tone="subdued">
                        Clientes
                      </Text>
                      <Text variant="heading2xl" fontWeight="bold">
                        {stat(stats.totalCustomers)}
                      </Text>
                      {!loading && stats.newCustomersInPeriod > 0 ? (
                        <Text variant="bodySm" tone="subdued">
                          {stats.newCustomersInPeriod} nuevos en el periodo
                        </Text>
                      ) : null}
                    </BlockStack>
                  </Card>
                </Box>
              </InlineStack>
            </BlockStack>

            <BlockStack gap="300">
              <InlineStack gap="400" wrap>
                <Box minWidth="300px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="400">
                      <Text variant="headingMd" fontWeight="semibold">
                        Resumen del periodo
                      </Text>
                      <BlockStack gap="200">
                        <InlineStack align="space-between">
                          <Text variant="bodyMd">Pedidos en el periodo</Text>
                          <Text variant="bodyMd" fontWeight="semibold">
                            {stat(stats.ordersInPeriod)}
                          </Text>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text variant="bodyMd">Ventas del periodo</Text>
                          <Text variant="bodyMd" fontWeight="semibold">
                            {stat(formatCurrency(stats.salesInPeriod))}
                          </Text>
                        </InlineStack>
                        <InlineStack align="space-between">
                          <Text variant="bodyMd">Periodo anterior</Text>
                          <Text variant="bodyMd" fontWeight="semibold">
                            {stat(formatCurrency(stats.previousPeriodSales))}
                          </Text>
                        </InlineStack>
                      </BlockStack>
                    </BlockStack>
                  </Card>
                </Box>

                <Box minWidth="300px" style={{ flexGrow: 1 }}>
                  <Card>
                    <BlockStack gap="400">
                      <Text variant="headingMd" fontWeight="semibold">
                        Acciones rápidas
                      </Text>
                      <BlockStack gap="200">
                        <Text variant="bodyMd" tone="subdued">
                          • Ver todas las órdenes
                        </Text>
                        <Text variant="bodyMd" tone="subdued">
                          • Gestionar productos
                        </Text>
                        <Text variant="bodyMd" tone="subdued">
                          • Ver estadísticas detalladas
                        </Text>
                        <Text variant="bodyMd" tone="subdued">
                          • Ir al punto de venta
                        </Text>
                      </BlockStack>
                    </BlockStack>
                  </Card>
                </Box>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </BlockStack>
    </Page>
  );
}
